import React, { useEffect, useState, useRef } from "react";
import { MessageSquare, Send } from "lucide-react"; // You'll need: npm install lucide-react
import { useDispatch } from "react-redux";
import CustomHookForRuralDetails from "../ruralDetailsCount/CustomHookForRuralDetails";
import { toast } from "react-toastify";

interface TypingEffectProps {
  textArray: string[];
  speed?: number;
  onSubmit?: (remarks: string) => void;
  onAllComplete?: () => void;
  componentID?: string | number;
  chartData?: any;
  chartValue?: any;
  chartFilter?: any;
}

const TypingEffect: React.FC<TypingEffectProps> = ({
  textArray,
  speed = 10,
  onSubmit,
  onAllComplete,
  componentID,
  chartData,
  chartValue,
  chartFilter,
}) => {
  const [typedItems, setTypedItems] = useState<string[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [showRemarksButton, setShowRemarksButton] = useState(false);
  const [showRemarksForm, setShowRemarksForm] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dispatch = useDispatch();
  const { aiInsightsRemarksSubmit } = CustomHookForRuralDetails();
  // Typing effect logic
  useEffect(() => {
    if (currentItemIndex >= textArray.length) {
      setShowRemarksButton(true);
      onAllComplete?.();
      return;
    }

    const fullText = textArray[currentItemIndex];
    let i = 0;
    const interval = setInterval(() => {
      setCurrentText((prev) => prev + fullText[i]);
      i++;
      if (i >= fullText.length) {
        clearInterval(interval);
        setTypedItems((prev) => [...prev, fullText]);
        setCurrentText("");
        setCurrentItemIndex((prev) => prev + 1);
      }
    }, speed);

    return () => clearInterval(interval);
  }, [currentItemIndex, textArray, speed, onAllComplete]);

  // Auto-focus when form opens
  useEffect(() => {
    if (showRemarksForm && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 200);
    }
  }, [showRemarksForm]);
  const handleSubmit = async () => {
    if (!remarks.trim()) return;
    setIsSubmitting(true);
    try {
      let data = {
        user_remarks: remarks.trim(),
        model_response: textArray,
        json_data: { chartData, chartValue, chartFilter },
      };
      const response = await aiInsightsRemarksSubmit(componentID!, data);
      // dispatch(response);
      if (response?.status === 1) {
        toast.success(response.message);
      } else {
        toast.success("Remarks submitted successfully");
      }
      onSubmit?.(remarks.trim());
      setShowRemarksForm(false);
      setRemarks("");
    } catch (error) {
      toast.error("Failed to submit remarks");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="max-w-3xl mx-2 py-2">
      {/* Typing Insights */}
      <ul className="space-y-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
        {typedItems.map((item, idx) => (
          <li
            key={idx}
            className="list-disc list-inside animate-fade-in"
            dangerouslySetInnerHTML={{ __html: item }}
          />
        ))}

        {currentText && (
          <li className="list-none flex items-left">
            <span className="text-blue-600 font-mono animate-pulse"></span>
            <span
              className="ml-2"
              dangerouslySetInnerHTML={{ __html: currentText }}
            />
            <span className="ml-1 text-blue-600 animate-pulse"></span>
          </li>
        )}
      </ul>

      {/* Remarks Button - Appears after typing finishes */}
      {showRemarksButton && !showRemarksForm && (
        <div className="flex justify-left mt-2 animate-fade-in">
          <button
            onClick={() => setShowRemarksForm(true)}
            className="flex items-left gap-3 px-3 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 
                       hover:from-blue-700 hover:to-indigo-700 text-white font-medium 
                       rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 
                       transform hover:-translate-y-1 bg-green-500"
          >
            <MessageSquare size={22} />
            <span>Add Your Remarks</span>
          </button>
        </div>
      )}

      {/* Remarks Form - Slides in when button clicked */}
      {showRemarksForm && (
        <div
          className="mt-5 p-3 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 
                        border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl 
                        animate-slide-up"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-800 dark:text-gray-100">
            <MessageSquare size={20} className="text-blue-600" />
            Your Remarks & Feedback
          </h3>

          <textarea
            ref={textareaRef}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            placeholder="Share your thoughts, feedback, or next steps based on these insights..."
            className="w-full px-3 py-4 border border-gray-300 dark:border-gray-600 rounded-xl
                       focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 focus:outline-none
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                handleSubmit();
              }
            }}
          />

          <div className="mt-5 flex justify-between items-center">
            <button
              onClick={() => {
                setShowRemarksForm(false);
                setRemarks("");
              }}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !remarks.trim()}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 
                         disabled:bg-gray-400 disabled:cursor-not-allowed
                         text-white font-medium rounded-xl transition shadow-md"
            >
              {isSubmitting ? (
                "Submitting..."
              ) : (
                <>
                  <Send size={18} />
                  Submit
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TypingEffect;
